import {
  DEFAULT_IMAGE_DURATION_MS,
  builtInMenuPack,
  evaluateSchedules,
  scheduleMatchesAt,
  stableStringify,
  type EvaluableSchedule,
  type ManifestPlaylistItem,
  type MenuBoardContent,
  type MenuPack,
  type PlayerManifest,
  type ScalingMode,
  type Transition,
} from '@signage/shared';
import { DateTime } from 'luxon';

/**
 * Turns a manifest into a concrete, ordered list of things to show right now.
 *
 * This runs entirely on the device from locally stored data, so scheduled campaigns start and
 * end correctly with no network at all (spec §16).
 */

/** One page of a menu board, with the cached files it needs. */
export interface MenuPlaybackPage {
  board: MenuBoardContent;
  page: number;
  pageCount: number;
  /** The orientation the pages were cut for; the renderer draws the same page whatever its box. */
  orientation: 'landscape' | 'portrait';
  /** Media id → where the host keeps the file; only images that are actually cached. */
  images: Record<string, { source: string; sha256: string }>;
  /**
   * The downloaded menu pack the pages were cut with, so the surface draws with the same one;
   * null for the renderer built into the player.
   */
  packSha256?: string | null;
}

export interface PlaybackItem {
  /** Stable key for the renderer; changes when the item's content changes. */
  key: string;
  /** The file played, or for a menu page the menu's id. */
  mediaId: string;
  kind: 'image' | 'video' | 'menu';
  mimeType: string;
  /** Local file path resolved by the host, or null when the file is not cached. */
  source: string | null;
  sha256: string;
  durationMs: number;
  /** True when the duration should come from the video itself. */
  useNaturalDuration: boolean;
  transition: Transition;
  transitionMs: number;
  scaling: ScalingMode;
  muted: boolean;
  playlistId: string | null;
  slideshowId: string | null;
  scheduleId: string | null;
  name?: string;
  /** Present when `kind` is `menu`. */
  menu?: MenuPlaybackPage;
}

export interface PlaybackPlan {
  scheduleId: string | null;
  scheduleName: string | null;
  contentType: 'playlist' | 'slideshow' | 'media' | 'menu' | null;
  contentId: string | null;
  playlistId: string | null;
  loop: boolean;
  items: PlaybackItem[];
  /** UTC ISO instant at which the plan may change; the player wakes up then. */
  nextBoundary: string | null;
  timezone: string;
  /** Media ids referenced by the plan but missing from the cache. */
  unavailableMediaIds: string[];
  /**
   * The downloaded menu pack the menu pages were cut with, as source for the playback surface
   * to run. Handed over with the plan rather than fetched, so every host delivers it the same
   * way; absent when the built-in designs are in use.
   */
  menuPack?: { sha256: string; source: string } | null;
}

export interface PlanOptions {
  /** Resolve a media id to a local path; return null when it is not available. */
  resolvePath: (mediaId: string, sha256: string) => string | null;
  now?: Date;
  /** The menu designs to cut boards with; the built-in ones when absent. */
  menuPack?: { pack: MenuPack; sha256: string; source: string };
}

const MAX_NESTING_DEPTH = 5;

export function buildPlaybackPlan(
  manifest: PlayerManifest | null,
  options: PlanOptions,
): PlaybackPlan {
  const timezone = manifest?.location?.timezone ?? 'UTC';
  const empty: PlaybackPlan = {
    scheduleId: null,
    scheduleName: null,
    contentType: null,
    contentId: null,
    playlistId: null,
    loop: true,
    items: [],
    nextBoundary: null,
    timezone,
    unavailableMediaIds: [],
  };
  if (!manifest) return empty;

  const evaluable: EvaluableSchedule[] = manifest.schedules.map((s) => ({
    id: s.scheduleId,
    priority: s.priority,
    always: s.always,
    contentType: s.contentType,
    contentId: s.contentId,
    startDate: s.startDate,
    endDate: s.endDate,
    daysOfWeek: s.daysOfWeek,
    timeRanges: s.timeRanges,
    updatedAt: s.updatedAt,
    name: s.name,
  }));

  const now = options.now ?? new Date();
  const decision = evaluateSchedules(evaluable, { timezone, now });

  if (!decision.active) {
    return { ...empty, nextBoundary: decision.nextBoundary };
  }

  const mediaById = new Map(manifest.media.map((m) => [m.mediaId, m]));
  const playlistById = new Map(manifest.playlists.map((p) => [p.playlistId, p]));
  const slideshowById = new Map(manifest.slideshows.map((s) => [s.slideshowId, s]));
  const menuById = new Map((manifest.menus ?? []).map((m) => [m.menuId, m]));
  const local = DateTime.fromJSDate(now, { zone: 'utc' }).setZone(timezone);

  const items: PlaybackItem[] = [];
  const unavailable: string[] = [];
  let usesMenuPack = false;
  let loop = true;
  /** The schedule whose content is being expanded, so each item reports where it came from. */
  let currentSchedule: EvaluableSchedule | null = null;
  let playlistId: string | null = null;

  const pushMedia = (params: {
    mediaId: string;
    durationMs: number | null | undefined;
    transition: Transition;
    transitionMs: number;
    scaling: ScalingMode;
    muted: boolean;
    playlistId: string | null;
    slideshowId: string | null;
    itemId: string;
  }): void => {
    const media = mediaById.get(params.mediaId);
    if (!media) {
      unavailable.push(params.mediaId);
      return;
    }
    const source = options.resolvePath(media.mediaId, media.sha256);
    if (!source) {
      // Not cached: skip it rather than showing a broken frame. The sync engine will fetch it.
      unavailable.push(params.mediaId);
      return;
    }
    const isVideo = media.kind === 'video';
    const explicit = params.durationMs ?? null;
    items.push({
      key: `${params.itemId}:${media.sha256}`,
      mediaId: media.mediaId,
      kind: media.kind,
      mimeType: media.mimeType,
      source,
      sha256: media.sha256,
      durationMs:
        explicit ??
        (isVideo ? (media.durationMs ?? DEFAULT_IMAGE_DURATION_MS) : DEFAULT_IMAGE_DURATION_MS),
      useNaturalDuration: isVideo && explicit === null,
      transition: params.transition,
      transitionMs: params.transitionMs,
      scaling: params.scaling,
      muted: params.muted,
      playlistId: params.playlistId,
      slideshowId: params.slideshowId,
      scheduleId: currentSchedule?.id ?? null,
      name: media.name,
    });
  };

  /**
   * A menu board becomes one item per page. Missing photos do not hold the board back: it is
   * drawn without them and they appear once downloaded, because the cached set is part of the key.
   */
  /** `pageDurationMs` overrides the board's own, for a board placed in a playlist. */
  const expandMenu = (menuId: string, parentPlaylistId: string | null, pageDurationMs?: number | null): void => {
    const board = menuById.get(menuId);
    if (!board) return;
    const images: MenuPlaybackPage['images'] = {};
    const menuPack: { pack: MenuPack; sha256: string | null } = options.menuPack ?? { pack: builtInMenuPack(), sha256: null };
    usesMenuPack ||= Boolean(options.menuPack);
    for (const id of menuPack.pack.menuMediaIds(board)) {
      const media = mediaById.get(id);
      const source = media ? options.resolvePath(media.mediaId, media.sha256) : null;
      if (media && source) images[id] = { source, sha256: media.sha256 };
      else unavailable.push(id);
    }
    const orientation = manifest.screen.orientation;
    const pages = menuPack.pack.paginateMenu(board, orientation);
    const version = hashString(
      stableStringify({ board, cached: Object.keys(images).sort(), pack: menuPack.sha256 }),
    );
    pages.forEach((_, page) => {
      items.push({
        key: `menu:${board.menuId}:${page}:${version}`,
        mediaId: board.menuId,
        kind: 'menu',
        mimeType: 'text/html',
        source: `menu:${board.menuId}`,
        sha256: '',
        durationMs: pageDurationMs ?? board.settings.pageDurationMs,
        useNaturalDuration: false,
        transition: 'fade',
        transitionMs: 600,
        scaling: 'fill',
        muted: true,
        playlistId: parentPlaylistId,
        slideshowId: null,
        scheduleId: currentSchedule?.id ?? null,
        name: board.name,
        menu: { board, page, pageCount: pages.length, orientation, images, packSha256: menuPack.sha256 },
      });
    });
  };

  const expandSlideshow = (slideshowId: string, parentPlaylistId: string | null): void => {
    const slideshow = slideshowById.get(slideshowId);
    if (!slideshow) return;
    for (const slide of [...slideshow.items].sort((a, b) => a.position - b.position)) {
      pushMedia({
        mediaId: slide.mediaId,
        durationMs: slide.durationMs,
        transition: slide.transition,
        transitionMs: slide.transitionMs,
        scaling: slide.scaling,
        muted: true,
        playlistId: parentPlaylistId,
        slideshowId: slideshow.slideshowId,
        itemId: slide.id,
      });
    }
  };

  /** Per-item availability windows are evaluated with the same rules as schedules. */
  const itemIsAvailable = (item: ManifestPlaylistItem): boolean => {
    const hasWindow =
      Boolean(item.startDate) ||
      Boolean(item.endDate) ||
      (item.daysOfWeek?.length ?? 0) > 0 ||
      (item.timeRanges?.length ?? 0) > 0;
    if (!hasWindow) return true;
    return scheduleMatchesAt(
      {
        id: item.id,
        priority: 0,
        always: false,
        contentType: 'media',
        contentId: item.mediaId ?? item.id,
        startDate: item.startDate,
        endDate: item.endDate,
        daysOfWeek: item.daysOfWeek,
        timeRanges: item.timeRanges,
        updatedAt: '1970-01-01T00:00:00.000Z',
      },
      local,
    );
  };

  const expandPlaylist = (id: string, depth: number, visited: Set<string>): void => {
    if (depth > MAX_NESTING_DEPTH || visited.has(id)) return;
    visited.add(id);
    const playlist = playlistById.get(id);
    if (!playlist) return;
    if (depth === 0) {
      loop = playlist.loop;
      playlistId = playlist.playlistId;
    }
    for (const item of [...playlist.items].sort((a, b) => a.position - b.position)) {
      if (!itemIsAvailable(item)) continue;
      if (item.itemType === 'media' && item.mediaId) {
        pushMedia({
          mediaId: item.mediaId,
          durationMs: item.durationMs,
          transition: item.transition,
          transitionMs: item.transitionMs,
          scaling: item.scaling,
          muted: item.muted,
          playlistId: playlist.playlistId,
          slideshowId: null,
          itemId: item.id,
        });
      } else if (item.itemType === 'slideshow' && item.slideshowId) {
        expandSlideshow(item.slideshowId, playlist.playlistId);
      } else if (item.itemType === 'playlist' && item.playlistId) {
        expandPlaylist(item.playlistId, depth + 1, visited);
      } else if (item.itemType === 'menu' && item.menuId) {
        expandMenu(item.menuId, playlist.playlistId, item.durationMs);
      }
    }
  };

  const active = decision.active;
  // Schedules at the same top priority take turns: each one's content in order, then round again.
  const playing = decision.playing.length > 0 ? decision.playing : [active];
  for (const schedule of playing) {
    currentSchedule = schedule;
    if (schedule.contentType === 'playlist') {
      expandPlaylist(schedule.contentId, 0, new Set());
    } else if (schedule.contentType === 'slideshow') {
      const slideshow = slideshowById.get(schedule.contentId);
      // A slideshow that plays once only stops the screen when it is the only thing on.
      if (playing.length === 1) loop = slideshow?.loop ?? true;
      expandSlideshow(schedule.contentId, null);
    } else if (schedule.contentType === 'menu') {
      expandMenu(schedule.contentId, null);
    } else if (schedule.contentType === 'media') {
      pushMedia({
        mediaId: schedule.contentId,
        durationMs: null,
        transition: 'none',
        transitionMs: 0,
        scaling: manifest.screen.scaling,
        muted: true,
        playlistId: null,
        slideshowId: null,
        itemId: `direct-${schedule.contentId}`,
      });
    }
  }

  return {
    scheduleId: active.id,
    scheduleName: playing.map((s) => s.name).filter(Boolean).join(' + ') || null,
    contentType: active.contentType,
    contentId: active.contentId,
    playlistId,
    loop,
    items,
    nextBoundary: decision.nextBoundary,
    timezone,
    unavailableMediaIds: [...new Set(unavailable)],
    menuPack:
      usesMenuPack && options.menuPack ? { sha256: options.menuPack.sha256, source: options.menuPack.source } : null,
  };
}

/** A stable signature so the renderer can tell whether the plan actually changed. */
export function planSignature(plan: PlaybackPlan): string {
  return [
    plan.scheduleId ?? '-',
    plan.contentId ?? '-',
    plan.loop ? '1' : '0',
    plan.items.map((i) => `${i.key}@${i.durationMs}`).join('|'),
  ].join('#');
}

/** FNV-1a; only needs to notice that a board changed, not resist anyone. */
function hashString(value: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}
